import atob from 'atob'

import { parseProductVariantFromShopify } from '~/helpers/shopify/product'

export const state = () => ({
  wishlistIds: [],
  wishlistData: [],
  wishlistedItem: null,
  shopifyWishlistCreated: false,
})

export const getters = {
  getWishlistProducts(state) {
    return state.wishlistData
  },
  getWishlistIds(state) {
    return state.wishlistIds
  },
  getWishlistTotal(state) {
    return state.wishlistIds.length
  },
  getWishlistedItem(state) {
    return state.wishlistedItem
  },
}

export const mutations = {
  addToWishlist(state, variantId) {
    state.wishlistIds.push(variantId)
  },
  removeFromWishlist(state, variantId) {
    const variantIndex = state.wishlistIds.indexOf(variantId)
    state.wishlistIds.splice(variantIndex, 1)
  },
  setWishlistData(state, wishlistData) {
    state.wishlistData = wishlistData
  },
  setWishlistIds(state, wishlistIds) {
    state.wishlistIds = wishlistIds
  },
  setWishlistedItem(state, wishlistedItem) {
    state.wishlistedItem = wishlistedItem
  },
}

export const actions = {
  initialize({ dispatch, rootGetters }) {
    dispatch('syncWishlistCookies')
    if (rootGetters['customer/getCustomer']) dispatch('syncWishlistShopify')
  },
  async fetchWishlistData({ commit, getters }) {
    try {
      if (isWishlistActive(this.$i18n.locale, this.$config)) {
        const wishlistIds = getters.getWishlistIds
        if (wishlistIds.length <= 0) {
          commit('setWishlistData', [])
          return
        }
        await this.$axios
          .post('/api/shopify/getVariantsFromIds', {
            wishlistIds,
            locale: this.app.i18n.locale,
          })
          .then((res) => {
            if (res.data.wishlistData) {
              const wishlistData = res.data.wishlistData
              const parsedData = {}
              for (const item in wishlistData) {
                if (wishlistData[item].parentProduct.productType !== 'Giftcard')
                  parsedData[item] = parseProductVariantFromShopify(
                    wishlistData[item],
                    this.app.i18n.locale
                  )
              }
              commit('setWishlistData', parsedData)
            }
          })
      }
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'store/wishlist/fetchWishlistData',
        level: 'warning',
        extra: 'Failed to fetch wishlistData',
      })
    }
  },
  addProductToWishlist({ commit, dispatch, getters }, args) {
    try {
      const product = args.product || null
      const variant = args.variant || null

      if (!product || !variant || !args) return false

      const countryCode = this.app.i18n.locale.substring(3, 5)
      const wishlistSlug = `lole-wishlist-${countryCode}`
      const wishlist = getters.getWishlistIds

      if (!wishlist.includes(variant.id)) {
        commit('addToWishlist', variant.id)
        dispatch('updateLocalWishlist', { wishlist, wishlistSlug })
        dispatch('syncWishlistShopify')

        try {
          this.$gtmEvents.UAAddToWishlist(product, variant, {
            page: 'product',
          })

          /**
           * Leave FB & Klaviyo Events commented
           * pending implementation request
           */
          // this.$fbEvents.AddToWishlist(product, variant, quantity)
          // const customer = rootGetters['customer/getCustomer']
          // if (customer)
          //   this.$klaviyoEvents.AddToWishlist(
          //     product,
          //     variant,
          //     quantity,
          //     customer
          //   )
        } catch (e) {
          this.app.$sentryException(e, {
            source: 'store - wishlist',
            level: 'warning',
            extra: 'GTM failed to add to wishlist',
          })
        }
      }
    } catch (error) {
      this.app.$sentryException(error, {
        source: 'store - wishlist',
        level: 'warning',
        extra: 'Failed to add product to the wishlist',
      })
      dispatch('notification/pushSnackbarError', {}, { root: true }) // TODO: ADD ERROR MESSAGE
    }
  },
  removeProductFromWishlist({ commit, dispatch, getters }, variantId) {
    try {
      const countryCode = this.app.i18n.locale.substring(3, 5)
      const wishlistSlug = `lole-wishlist-${countryCode}`
      const wishlist = getters.getWishlistIds

      /**
       * Update wishlist data locally
       * without having to wait for api call
       * (smoother animation improvement)
       */
      const wishlistData = Object.entries(getters.getWishlistProducts)
      const filteredWl = wishlistData.filter((item) => {
        return item[1].variantId !== variantId
      })
      const updatedWishlistData = Object.fromEntries(filteredWl)
      commit('setWishlistData', updatedWishlistData)

      if (wishlist.includes(variantId)) {
        commit('removeFromWishlist', variantId)
        dispatch('updateLocalWishlist', { wishlist, wishlistSlug })
        dispatch('syncWishlistShopify', 'remove')
      }
    } catch (error) {
      this.app.$sentryException(error, {
        source: 'store - wishlist',
        level: 'warning',
        extra: 'Failed to remove product from the wishlist',
      })
      dispatch('notification/pushSnackbarError', {}, { root: true }) // TODO: ADD ERROR MESSAGE
    }
  },
  async syncWishlistShopify(
    { commit, dispatch, getters, rootGetters },
    wlAction
  ) {
    const customer = rootGetters['customer/getCustomer']
    if (!customer) return

    const localWishlist = getters.getWishlistIds // this is always the most true reactive state of the Wishlist
    const shopifyWishlist = customer.wishlist // this is the remote (shopify) wishlist which is not the latest
      ? JSON.parse(customer.wishlist.value)
      : []

    const syncedWishlist =
      wlAction === 'remove'
        ? localWishlist
        : [...new Set([...shopifyWishlist, ...localWishlist])]

    // decode id
    syncedWishlist.forEach((id, index) => {
      if (!id.includes('gid://shopify/ProductVariant/')) {
        syncedWishlist[index] = atob(id)
      }
    })

    commit('setWishlistIds', syncedWishlist)

    const apiAction = customer.wishlist
      ? 'updateRemoteWishlist'
      : 'createRemoteWishlist'

    if (localWishlist !== shopifyWishlist) {
      const args = { customer, wishlist: syncedWishlist }

      // This is the Admin API call
      await dispatch(apiAction, args)
    }
  },
  syncWishlistCookies({ commit, dispatch, getters }) {
    const wishlistCookies = this.app.$cookies.get(
      `lole-wishlist-${this.app.i18n.locale.substring(3, 5)}`
    )
    if (wishlistCookies) {
      // decode id
      wishlistCookies.forEach((id, index) => {
        if (!id.includes('gid://shopify/ProductVariant/')) {
          wishlistCookies[index] = atob(id)
        }
      })
      commit('setWishlistIds', wishlistCookies)
    }
  },
  setCurrentWishlistItem({ commit }, wishlistItem) {
    commit('setWishlistedItem', wishlistItem)
  },
  unsetCurrentWishlistItem({ commit }) {
    commit('setWishlistedItem', null)
  },
  async updateRemoteWishlist({ dispatch }, args) {
    if (!args.customer || !args.wishlist) return
    // if (args.wishlist.length <= 0) return
    if (args.wishlist === args.customer.wishlist.value) return
    try {
      await this.$axios
        .post('/api/shopify/updateCustomerMetafield', {
          locale: this.app.i18n.locale,
          customerId: args.customer.id,
          metafieldId: args.customer.wishlist.id,
          value: args.wishlist,
        })
        .then((res) => {
          if (res.status === 200) {
            dispatch('customer/syncCustomerWishlist', null, {
              root: true,
            })
          }
        })
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'store/wishlist/updateRemoteWishlist',
        level: 'warning',
        extra: 'Failed to update Shopify Wishlist for customer (metafield)',
      })
    }
  },
  async createRemoteWishlist({ dispatch }, args) {
    if (!args.customer || !args.wishlist) return
    try {
      await this.$axios
        .post('/api/shopify/createCustomerMetafield', {
          locale: this.app.i18n.locale,
          customerId: args.customer.id,
          namespace: 'lole_shop',
          key: 'wishlist',
          type: 'json',
          value: args.wishlist,
        })
        .then((res) => {
          if (res.status === 200) {
            dispatch('customer/syncCustomerWishlist', null, {
              root: true,
            })
          }
        })
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'store/wishlist/createRemoteWishlist',
        level: 'warning',
        extra: 'Failed to crete Shopify Wishlist for customer (metafield)',
      })
    }
  },
  updateLocalWishlist({ dispatch, rootGetters }, args) {
    if (rootGetters['customer/getCustomer']) return false
    try {
      if (!args.wishlist || !args.wishlistSlug) return
      this.app.$cookies.set(`${args.wishlistSlug}`, args.wishlist, {
        maxAge: 60 * 60 * 24 * 30,
        path: '/',
      })
    } catch (error) {
      this.app.$sentryException(error, {
        source: 'store - wishlist',
        level: 'warning',
        extra: 'Failed to update the local wishlist (cookies)',
      })
      dispatch('notification/pushSnackbarError', {}, { root: true }) // TODO: ADD ERROR MESSAGE
    }
  },
  destroyLocalWishlist({ commit, dispatch }, { logout }) {
    try {
      const countryCode = this.app.i18n.locale.substring(3, 5)
      this.app.$cookies.remove(`lole-wishlist-${countryCode}`)
      if (logout) {
        commit('setWishlistIds', [])
      }
    } catch (error) {
      this.app.$sentryException(error, {
        source: 'store - wishlist',
        level: 'warning',
        extra: 'Failed to destroy the local wishlist (cookies)',
      })
      dispatch('notification/pushSnackbarError', {}, { root: true }) // TODO: ADD ERROR MESSAGE
    }
  },
  moveProductToCart({ dispatch }, product) {
    // const productForCart = Utils.prepareProductForCart(product)
    // const cartPayload = Utils.buildCartPayload(productForCart)
    // dispatch('checkout/addProductToCart', cartPayload, { root: true })
    dispatch('removeProductFromWishlist', product.variantId)
  },
  moveProductToWishlist({ dispatch }, product) {
    const args = { product, variant: product.variant }
    dispatch('addProductToWishlist', args)
  },
}

// const Utils = {
//   prepareProductForCart(product) {
//     const model = {
//       sample_color: product.variantColor.color_sample || null,
//       normalized_color: product.variantColor.normalized_color || null,
//       handle: product.productHandle,
//       title: product.title,
//       categories: ['Top', 'T-shirt'],
//       medias: [{ position: 1, src: product.image.url, type: 'image' }],
//       price: product.price.amount,
//       discount_price: product.price.discountPrice,
//       selectedVariant: {
//         quantity: 1,
//         size: product.variantSize,
//         variant_id: product.variantId,
//         barcode: product.barcode,
//       },
//     }
//     return model
//   },
//   buildCartPayload(cartProduct) {
//     const payload = {
//       location: 'wishlist',
//       product: cartProduct,
//       selectedVariant: cartProduct.selectedVariant,
//     }

//     return payload
//   },
// }

export function isWishlistActive(locale, config) {
  try {
    if (
      (locale.includes('ca') && config.DISABLED_WISHLIST_CA) ||
      (locale.includes('us') && config.DISABLED_WISHLIST_US) ||
      (locale.includes('eu') && config.DISABLED_WISHLIST_EU)
    )
      return false
    return true
  } catch (e) {
    return false
  }
}
